import { useMemo, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Autocomplete } from "@/components/ui/autocomplete";
import { Button } from "@akuna/ui";

export type BreedOption = { id: string; name: string };

interface BreedAutocompleteProps {
  breeds: BreedOption[];
  isLoading?: boolean;
  value: string;
  selectedBreedId?: string;
  onChange: (value: string) => void;
  onSelectBreed: (breed: BreedOption, displayName: string) => void;
  onCreateNew?: (name: string) => void;
  isCreating?: boolean;
  placeholder?: string;
  emptyMessage?: ReactNode;
  disabled?: boolean;
  onBlur?: () => void;
  popoverPortalled?: boolean;
}

export function BreedAutocomplete({
  breeds,
  isLoading = false,
  value,
  selectedBreedId,
  onChange,
  onSelectBreed,
  onCreateNew,
  isCreating = false,
  placeholder = "Digite o nome da raça...",
  emptyMessage: emptyMessageProp,
  disabled = false,
  onBlur,
  popoverPortalled = true,
}: BreedAutocompleteProps) {
  const { t } = useTranslation("patients");
  const sortedBreeds = useMemo(
    () =>
      [...breeds].sort((a, b) =>
        (a.name ?? "").localeCompare(b.name ?? "", "pt-BR", { sensitivity: "base" }),
      ),
    [breeds],
  );

  const options = useMemo(
    () =>
      sortedBreeds.map((breed) => ({
        label: breed.name,
        value: breed.id,
      })),
    [sortedBreeds],
  );

  const breedsById = useMemo(
    () => new Map(sortedBreeds.map((breed) => [breed.id, breed])),
    [sortedBreeds],
  );

  const resolvedPlaceholder = placeholder ?? t("breedAutocomplete.placeholder");

  const emptyMessage = isLoading ? (
    <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />
      {t("breedAutocomplete.loading")}
    </div>
  ) : emptyMessageProp ? (
    emptyMessageProp
  ) : (
    <div className="px-3 py-4 text-sm text-muted-foreground">
      <p>{t("breedAutocomplete.empty")}</p>
    </div>
  );

  const hasExactMatch = useMemo(() => {
    if (!value.trim()) return true;
    const searchValue = value.toLowerCase().trim();
    return sortedBreeds.some((breed) => breed.name.toLowerCase().trim() === searchValue);
  }, [sortedBreeds, value]);

  const canCreate = !disabled && !!onCreateNew && value.trim() && !hasExactMatch;

  const footer = onCreateNew ? (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      disabled={!canCreate || isCreating}
      className="w-full justify-start text-primary hover:bg-accent"
      onClick={(event) => {
        event.stopPropagation();
        if (!canCreate || !onCreateNew) return;
        onCreateNew(value);
      }}
    >
      {isCreating ? (
        <span className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t("breedAutocomplete.creating")}
        </span>
      ) : canCreate ? (
        <>{t("breedAutocomplete.createNew", { value })}</>
      ) : (
        <>{t("breedAutocomplete.createButton")}</>
      )}
    </Button>
  ) : null;

  return (
    <Autocomplete
      options={options}
      value={value}
      onChange={onChange}
      onSelect={(option) => {
        const breed = breedsById.get(option.value);
        if (!breed) return;
        onSelectBreed(breed, option.label);
      }}
      selectedValue={selectedBreedId ?? ""}
      placeholder={resolvedPlaceholder}
      emptyMessage={emptyMessage}
      footer={footer}
      disabled={disabled}
      onBlur={onBlur}
      popoverPortalled={popoverPortalled}
    />
  );
}
